import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { cn } from 'ui'
import { useMeasurePage } from '../../hooks/useMeasurePage'
import { PDFStoreProps } from '../../stores/pdf.store'
import { PageCaption, PageCaptionAttribute } from './PageCaption'
import { PageContainer } from './PageContainer'
import { buildPagePortalId } from './Viewer'
import { useDeviceDetect } from '@autosign/core/hooks/useDeviceDetect'

pdfjs.GlobalWorkerOptions.workerSrc = '/bin/pdf.worker.min.js'

export interface PDFRendererProps {
  headerHeight: `${number}rem`
  children?: React.ReactNode
  onPageBoundaryChange?: (
    pageId: string,
    boundary: PDFStoreProps['boundaries'][number]
  ) => void
  onDocumentReady?: () => void
  onPageViewportIn?: (page: number) => void
  zoom: number
  pdf: {
    id: string
    name: string
    url: string
    pages: {
      document_id: string
      page: number
      url: string
      id: string
      width: number
      height: number
    }[]
  }
}

type DocumentPage = PDFRendererProps['pdf']['pages'][number]

const PageRender = ({
  index,
  pdf,
  zoom,
  onPageViewportIn,
  onPageBoundaryChange,
  page,
}: {
  index: number
  page: DocumentPage
} & PDFRendererProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const { inViewRef, setIsLoading } = useMeasurePage({
    pageRef: canvasRef,
    documentPage: page,
    index,
    onPageBoundaryChange,
    onPageViewportIn,
  })

  return (
    <div className='relative' ref={inViewRef}>
      <Page
        onLoadStart={() => setIsLoading(true)}
        onLoadSuccess={() => setIsLoading(false)}
        canvasRef={canvasRef}
        loading={() => ''}
        scale={zoom}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        pageNumber={index + 1}
      >
        <div
          className='w-full h-full absolute top-0 left-0'
          id={buildPagePortalId(pdf.id, index)}
        />
        <PageCaption className='absolute'>
          <PageCaptionAttribute>{pdf.name}</PageCaptionAttribute>
          <PageCaptionAttribute>
            {index + 1} de {pdf.pages.length}
          </PageCaptionAttribute>
        </PageCaption>
      </Page>
    </div>
  )
}

export const NativeRenderer = ({
  children,
  zoom,
  pdf,
  onPageViewportIn,
  headerHeight,
  onDocumentReady,
  onPageBoundaryChange,
}: {
  children?: React.ReactNode
} & PDFRendererProps) => {
  const [numPages, setNumPages] = useState<number>()
  const [loading, setLoading] = useState(true)
  const device = useDeviceDetect()
  const [innerWidth, setInnerWidth] = useState('fit-content')

  const zoomFactor: { [key: number]: number } = {
    0.75: 0.75,
    1: 0.95,
    1.25: 1.25,
    1.5: 1.5,
    1.75: 1.75,
    2: 2,
  }

  useEffect(() => {
    if (device !== 'mobile') return setInnerWidth('fit-content')
    
    typeof window !== 'undefined' ? setInnerWidth(`${Math.round(window.innerWidth *  zoomFactor[zoom])}px`) : setInnerWidth('fit-content')
  }, [zoom, device])

  return (
    <>
      <style>
        {`
        .react-pdf__Page {
          margin:0px auto !important;
          width: fit-content !important;
        }
        .react-pdf__Page__canvas {
          margin:0px auto !important;
          width: ${innerWidth} !important;
          height: auto !important;
        }
        `}
      </style>
      {children}
      <Document
        className={cn('opacity-0', {
          'opacity-100': !loading,
        })}
        loading={() => ''}
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages)
          setLoading(false)
          if (onDocumentReady) {
            onDocumentReady()
          }
        }}
        file={pdf.url}
      >
        <PageContainer>
          {new Array(numPages).fill(0).map((_, index) => {
            return (
              <PageRender
                page={pdf.pages[index]}
                onDocumentReady={onDocumentReady}
                onPageBoundaryChange={onPageBoundaryChange}
                headerHeight={headerHeight}
                zoom={zoom}
                pdf={pdf}
                onPageViewportIn={onPageViewportIn}
                key={`document-${pdf.id}-page-${index}`}
                index={index}
              />
            )
          })}
        </PageContainer>
      </Document>
    </>
  )
}
